import { jsx, jsxs } from "react/jsx-runtime";
import { motion } from "motion/react";
import { ArrowRight, Play } from "lucide-react";
const orbitItems = [
  { label: "Voice AI", icon: "\u{1F399}\uFE0F", deg: 0 },
  { label: "Chat AI", icon: "\u{1F4AC}", deg: 60 },
  { label: "Vision AI", icon: "\u{1F441}\uFE0F", deg: 120 },
  { label: "Automation", icon: "\u26A1", deg: 180 },
  { label: "Analytics", icon: "\u{1F4CA}", deg: 240 },
  { label: "CRM", icon: "\u{1F517}", deg: 300 }
];
function AIHero() {
  return /* @__PURE__ */ jsxs("section", { className: "relative min-h-screen flex items-center overflow-hidden bg-white pt-16", children: [
    /* @__PURE__ */ jsxs("div", { className: "absolute inset-0 pointer-events-none", children: [
      /* @__PURE__ */ jsx("div", { style: {
        position: "absolute",
        top: "-20%",
        right: "-10%",
        width: "700px",
        height: "700px",
        borderRadius: "50%",
        background: "radial-gradient(circle,rgba(111,92,247,0.08) 0%,transparent 65%)"
      } }),
      /* @__PURE__ */ jsx("div", { style: {
        position: "absolute",
        bottom: "-10%",
        left: "-5%",
        width: "500px",
        height: "500px",
        borderRadius: "50%",
        background: "radial-gradient(circle,rgba(168,85,247,0.06) 0%,transparent 65%)"
      } }),
      /* @__PURE__ */ jsx("div", { style: {
        position: "absolute",
        top: "30%",
        left: "20%",
        width: "300px",
        height: "300px",
        borderRadius: "50%",
        background: "radial-gradient(circle,rgba(111,92,247,0.04) 0%,transparent 65%)"
      } }),
      /* @__PURE__ */ jsx("div", { style: {
        position: "absolute",
        inset: 0,
        backgroundImage: "linear-gradient(rgba(111,92,247,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(111,92,247,0.03) 1px,transparent 1px)",
        backgroundSize: "48px 48px"
      } })
    ] }),
    /* @__PURE__ */ jsx("style", { children: `
        @keyframes orb-pulse { 0%,100%{box-shadow:0 0 40px 8px rgba(111,92,247,0.2),0 0 80px 20px rgba(111,92,247,0.08);} 50%{box-shadow:0 0 60px 16px rgba(111,92,247,0.3),0 0 120px 40px rgba(111,92,247,0.12);} }
        @keyframes ring1 { 0%,100%{transform:scale(1);opacity:0.3;} 50%{transform:scale(1.06);opacity:0.12;} }
        @keyframes ring2 { 0%,100%{transform:scale(1);opacity:0.2;} 50%{transform:scale(1.08);opacity:0.08;} }
        @keyframes ring3 { 0%,100%{transform:scale(1);opacity:0.12;} 50%{transform:scale(1.1);opacity:0.05;} }
        @keyframes orbit { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }
        @keyframes counter-orbit { from{transform:rotate(0deg);} to{transform:rotate(-360deg);} }
        @keyframes float-pill { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-4px);} }
        @keyframes gradient-shift { 0%,100%{background-position:0% 50%;} 50%{background-position:100% 50%;} }
        .gradient-text {
          background: linear-gradient(135deg,#6f5cf7 0%,#8b5cf6 40%,#a855f7 70%,#6f5cf7 100%);
          background-size: 200% 200%;
          animation: gradient-shift 4s ease-in-out infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      ` }),
    /* @__PURE__ */ jsxs("div", { className: "relative max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center w-full", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsxs(
          motion.div,
          {
            initial: { opacity: 0, y: -12 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.5 },
            className: "inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-xs font-semibold",
            style: { backgroundColor: "rgba(111,92,247,0.08)", border: "1px solid rgba(111,92,247,0.2)", color: "#6f5cf7", fontFamily: "'Manrope',sans-serif", letterSpacing: "0.05em" },
            children: [
              /* @__PURE__ */ jsx("span", { className: "w-1.5 h-1.5 rounded-full bg-[#6f5cf7] animate-pulse" }),
              "THE FUTURE OF ENTERPRISE AI IS HERE",
              /* @__PURE__ */ jsx("span", { className: "w-1.5 h-1.5 rounded-full bg-[#a855f7] animate-pulse" })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          motion.h1,
          {
            initial: { opacity: 0, y: 32 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.75, delay: 0.1 },
            style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(2.6rem,5.5vw,4.5rem)", fontWeight: 800, color: "#111827", lineHeight: 1.08, letterSpacing: "-0.03em", marginBottom: "1.25rem" },
            children: [
              "Transform Every",
              /* @__PURE__ */ jsx("br", {}),
              "Business with",
              " ",
              /* @__PURE__ */ jsx("span", { className: "gradient-text", children: "Intelligent AI" })
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          motion.p,
          {
            initial: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.7, delay: 0.22 },
            style: { fontFamily: "'Inter',sans-serif", fontSize: "1.1rem", color: "#6b7280", lineHeight: 1.75, maxWidth: "520px", marginBottom: "2.5rem" },
            children: "Automate customer support, surveillance, appointments, operations, and business workflows using enterprise AI solutions built specifically for your industry."
          }
        ),
        /* @__PURE__ */ jsxs(
          motion.div,
          {
            initial: { opacity: 0, y: 16 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.6, delay: 0.34 },
            className: "flex flex-wrap gap-4 mb-10",
            children: [
              /* @__PURE__ */ jsxs(
                "button",
                {
                  className: "flex items-center gap-2 px-7 py-3.5 rounded-2xl font-semibold text-base transition-all duration-200 hover:scale-105 hover:shadow-2xl active:scale-95",
                  style: { fontFamily: "'Plus Jakarta Sans',sans-serif", background: "linear-gradient(135deg,#6f5cf7 0%,#8b5cf6 100%)", color: "white", boxShadow: "0 8px 32px rgba(111,92,247,0.35)" },
                  children: [
                    "Book Free Demo ",
                    /* @__PURE__ */ jsx(ArrowRight, { size: 17 })
                  ]
                }
              ),
              /* @__PURE__ */ jsxs(
                "button",
                {
                  className: "flex items-center gap-2 px-7 py-3.5 rounded-2xl font-semibold text-base border transition-all duration-200 hover:border-primary hover:bg-secondary",
                  style: { fontFamily: "'Plus Jakarta Sans',sans-serif", borderColor: "#e5e7eb", color: "#374151" },
                  children: [
                    /* @__PURE__ */ jsx(Play, { size: 15, fill: "currentColor" }),
                    " Watch Demo"
                  ]
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          motion.div,
          {
            initial: { opacity: 0 },
            animate: { opacity: 1 },
            transition: { delay: 0.6 },
            className: "flex flex-wrap items-center gap-6",
            children: [
              /* @__PURE__ */ jsx("div", { className: "flex -space-x-2", children: ["photo-1559839734-2b71ea197ec2", "photo-1507003211169-0a1dd7228f2d", "photo-1487412720507-e7ab37603c6f", "photo-1472099645785-5658abf4ff4e"].map((id, i) => /* @__PURE__ */ jsx(
                "img",
                {
                  src: `https://images.unsplash.com/${id}?w=40&h=40&fit=crop&auto=format`,
                  alt: "customer",
                  className: "w-8 h-8 rounded-full border-2 border-white object-cover"
                },
                i
              )) }),
              /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 mb-0.5", children: [
                  "\u2605\u2605\u2605\u2605\u2605".split("").map((s, i) => /* @__PURE__ */ jsx("span", { style: { color: "#f59e0b", fontSize: "0.8rem" }, children: s }, i)),
                  /* @__PURE__ */ jsx("span", { style: { fontFamily: "'Manrope',sans-serif", fontWeight: 700, fontSize: "0.82rem", color: "#111827", marginLeft: "4px" }, children: "4.9/5" })
                ] }),
                /* @__PURE__ */ jsx("p", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.78rem", color: "#6b7280" }, children: "Trusted by 500+ enterprises across 12 industries" })
              ] })
            ]
          }
        )
      ] }),
      /* @__PURE__ */ jsxs(
        motion.div,
        {
          initial: { opacity: 0, scale: 0.85 },
          animate: { opacity: 1, scale: 1 },
          transition: { duration: 1, delay: 0.2 },
          className: "hidden lg:flex items-center justify-center relative",
          style: { height: "520px" },
          children: [
            /* @__PURE__ */ jsx("div", { className: "absolute w-[440px] h-[440px] rounded-full border", style: { borderColor: "rgba(111,92,247,0.12)", animation: "ring3 4s ease-in-out infinite" } }),
            /* @__PURE__ */ jsx("div", { className: "absolute w-[360px] h-[360px] rounded-full border", style: { borderColor: "rgba(111,92,247,0.18)", animation: "ring2 4s ease-in-out infinite 0.5s" } }),
            /* @__PURE__ */ jsx("div", { className: "absolute w-[280px] h-[280px] rounded-full border", style: { borderColor: "rgba(111,92,247,0.25)", animation: "ring1 4s ease-in-out infinite 1s" } }),
            /* @__PURE__ */ jsx("div", { className: "absolute w-[400px] h-[400px]", style: { animation: "orbit 18s linear infinite" }, children: orbitItems.map((item) => {
              const rad = item.deg * Math.PI / 180;
              const r = 200;
              const x = r * Math.cos(rad);
              const y = r * Math.sin(rad);
              return /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "absolute flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg",
                  style: {
                    fontFamily: "'Manrope',sans-serif",
                    backgroundColor: "white",
                    border: "1px solid rgba(111,92,247,0.15)",
                    color: "#374151",
                    boxShadow: "0 4px 16px rgba(111,92,247,0.1)",
                    left: `calc(50% + ${x}px - 44px)`,
                    top: `calc(50% + ${y}px - 16px)`,
                    animation: "counter-orbit 18s linear infinite",
                    whiteSpace: "nowrap"
                  },
                  children: [
                    /* @__PURE__ */ jsx("span", { children: item.icon }),
                    item.label
                  ]
                },
                item.label
              );
            }) }),
            /* @__PURE__ */ jsxs(
              "div",
              {
                className: "relative w-36 h-36 rounded-full flex items-center justify-center z-10",
                style: {
                  background: "radial-gradient(circle at 35% 35%,#9b8eff 0%,#6f5cf7 40%,#4c3ddb 80%,#2d1fb0 100%)",
                  animation: "orb-pulse 3s ease-in-out infinite",
                  boxShadow: "0 0 40px 8px rgba(111,92,247,0.3),0 0 80px 20px rgba(111,92,247,0.12)"
                },
                children: [
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "absolute w-12 h-8 rounded-full top-4 left-6 opacity-30",
                      style: { background: "radial-gradient(ellipse,white 0%,transparent 70%)" }
                    }
                  ),
                  /* @__PURE__ */ jsx("span", { style: { fontSize: "2.8rem" }, children: "\u{1F9E0}" })
                ]
              }
            ),
            /* @__PURE__ */ jsxs(
              "div",
              {
                className: "absolute bottom-8 left-0 rounded-2xl p-4 shadow-xl",
                style: { backgroundColor: "white", border: "1px solid rgba(111,92,247,0.1)", animation: "float-pill 4s ease-in-out infinite", boxShadow: "0 8px 32px rgba(111,92,247,0.12)" },
                children: [
                  /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: "1.4rem", color: "#6f5cf7" }, children: "500+" }),
                  /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.75rem", color: "#6b7280" }, children: "Enterprise Clients" })
                ]
              }
            ),
            /* @__PURE__ */ jsxs(
              "div",
              {
                className: "absolute top-12 right-0 rounded-2xl p-4 shadow-xl",
                style: { backgroundColor: "white", border: "1px solid rgba(168,85,247,0.12)", animation: "float-pill 5s ease-in-out infinite 1s", boxShadow: "0 8px 32px rgba(168,85,247,0.12)" },
                children: [
                  /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: "1.4rem", color: "#a855f7" }, children: "99.2%" }),
                  /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.75rem", color: "#6b7280" }, children: "AI Accuracy" })
                ]
              }
            ),
            /* @__PURE__ */ jsxs(
              "div",
              {
                className: "absolute top-1/2 right-2 rounded-2xl p-4 shadow-xl",
                style: { backgroundColor: "white", border: "1px solid rgba(16,185,129,0.15)", animation: "float-pill 6s ease-in-out infinite 2s", boxShadow: "0 8px 32px rgba(16,185,129,0.1)" },
                children: [
                  /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: "1.4rem", color: "#10b981" }, children: "24\xD77" }),
                  /* @__PURE__ */ jsx("div", { style: { fontFamily: "'Inter',sans-serif", fontSize: "0.75rem", color: "#6b7280" }, children: "AI Availability" })
                ]
              }
            )
          ]
        }
      )
    ] })
  ] });
}
export {
  AIHero
};
